import { db, updateAdminAction, useAuth } from "@repo/ui";
import {
	collection,
	doc,
	runTransaction,
	serverTimestamp,
	Timestamp,
} from "firebase/firestore";
import {
	AlertTriangle,
	CheckCircle2,
	CircleDot,
	ExternalLink,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { AdminAction } from "../types";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

type TaskFilter = "open" | "all";

function firestoreDate(value: unknown): Date | null {
	if (value instanceof Timestamp) return value.toDate();
	if (value instanceof Date) return value;
	if (typeof value !== "string") return null;
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function displayDate(value: Date | string | null | undefined): string {
	if (!value) return "—";
	const parsed = value instanceof Date ? value : new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleString();
}

export function AdminTasksPage() {
	const { adminActions, companies, subscriptions } = useData();
	const { user } = useAuth();
	const [filter, setFilter] = useState<TaskFilter>("open");
	const [updatingId, setUpdatingId] = useState<string | null>(null);

	const openCount = adminActions.filter(
		(action) => action.status === "open",
	).length;
	const visibleActions = useMemo(
		() =>
			adminActions
				.filter((action) => filter === "all" || action.status === "open")
				.sort((left, right) => {
					if (left.status !== right.status) {
						return left.status === "open" ? -1 : 1;
					}
					return (
						new Date(right.createdAt || 0).getTime() -
						new Date(left.createdAt || 0).getTime()
					);
				}),
		[adminActions, filter],
	);

	const resolveCancellation = async (action: AdminAction) => {
		const subscription = subscriptions.find(
			(candidate) => candidate.id === action.subscriptionId,
		);
		if (!subscription || subscription.companyId !== action.companyId) {
			throw new Error("The subscription for this cancellation was not found.");
		}

		const subscriptionRef = doc(db, "subscriptions", action.subscriptionId);
		const actionRef = doc(db, "adminActions", action.id);
		const lockRef = doc(db, "subscriptionActiveLocks", action.companyId);
		const auditRef = doc(collection(db, "auditLogs"));
		await runTransaction(db, async (transaction) => {
			const [storedSubscription, storedAction, lock] = await Promise.all([
				transaction.get(subscriptionRef),
				transaction.get(actionRef),
				transaction.get(lockRef),
			]);
			if (
				!storedSubscription.exists() ||
				storedSubscription.data().companyId !== action.companyId
			) {
				throw new Error(
					"The subscription for this cancellation was not found.",
				);
			}
			if (!storedAction.exists() || storedAction.data().status !== "open") {
				throw new Error("The cancellation task is no longer open.");
			}

			const now = serverTimestamp();
			transaction.update(subscriptionRef, {
				status: "canceled",
				cancelAtPeriodEnd: false,
				canceledAt: now,
				nextBillingDate: null,
				updatedAt: now,
			});
			transaction.update(actionRef, {
				status: "done",
				completedAt: now,
				completedBy: user?.uid || null,
				updatedAt: now,
			});
			if (lock.data()?.subscriptionId === action.subscriptionId) {
				transaction.delete(lockRef);
			}
			transaction.set(auditRef, {
				timestamp: now,
				userId: user?.uid,
				type: "subscription_canceled",
				companyId: action.companyId,
				data: {
					subscriptionId: action.subscriptionId,
					adminActionId: action.id,
				},
			});
		});
	};

	const resolveRenewal = async (action: AdminAction) => {
		const subscriptionRef = doc(db, "subscriptions", action.subscriptionId);
		const actionRef = doc(db, "adminActions", action.id);
		const auditRef = doc(collection(db, "auditLogs"));
		await runTransaction(db, async (transaction) => {
			const [subscription, storedAction] = await Promise.all([
				transaction.get(subscriptionRef),
				transaction.get(actionRef),
			]);
			if (
				!subscription.exists() ||
				subscription.data().companyId !== action.companyId
			) {
				throw new Error("The subscription for this renewal was not found.");
			}
			if (!storedAction.exists() || storedAction.data().status !== "open") {
				throw new Error("The renewal task is no longer open.");
			}

			const dueDate = firestoreDate(storedAction.data().dueDate);
			const nextBillingDate = firestoreDate(
				subscription.data().nextBillingDate,
			);
			if (
				!dueDate ||
				!nextBillingDate ||
				nextBillingDate.getTime() <= dueDate.getTime()
			) {
				throw new Error(
					"Update the subscription's next billing date after verifying the renewal in Grow.",
				);
			}

			const now = serverTimestamp();
			transaction.update(actionRef, {
				status: "done",
				completedAt: now,
				completedBy: user?.uid || null,
				updatedAt: now,
			});
			transaction.set(auditRef, {
				timestamp: now,
				userId: user?.uid,
				type: "subscription_renewal_completed",
				companyId: action.companyId,
				data: {
					subscriptionId: action.subscriptionId,
					adminActionId: action.id,
				},
			});
		});
	};

	const resolveTask = async (action: AdminAction) => {
		if (!user?.uid) {
			throw new Error("A signed-in system administrator is required.");
		}
		if (action.type === "cancellation_requested") {
			await resolveCancellation(action);
		} else if (action.type === "subscription_renewal") {
			await resolveRenewal(action);
		} else {
			await updateAdminAction(db, action.id, {
				status: "done",
				completedAt: new Date().toISOString(),
				completedBy: user.uid,
			});
		}
	};

	const toggleTask = async (action: AdminAction) => {
		setUpdatingId(action.id);
		try {
			if (action.status === "open") {
				await resolveTask(action);
				toast.success("Task marked as resolved.");
			} else {
				await updateAdminAction(db, action.id, {
					status: "open",
					completedAt: null,
					completedBy: null,
				});
				toast.success("Task reopened.");
			}
		} catch (error) {
			console.error("Failed to update admin task:", error);
			toast.error(
				error instanceof Error ? error.message : "Failed to update admin task.",
			);
		} finally {
			setUpdatingId(null);
		}
	};

	return (
		<div className="space-y-6">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
				<div>
					<h1>Admin Tasks</h1>
					<p className="text-slate-600">
						Review and manage operational tasks requiring administrator action.
					</p>
				</div>
				<div className="flex gap-2">
					<Button
						variant={filter === "open" ? "default" : "outline"}
						onClick={() => setFilter("open")}
					>
						Open ({openCount})
					</Button>
					<Button
						variant={filter === "all" ? "default" : "outline"}
						onClick={() => setFilter("all")}
					>
						All ({adminActions.length})
					</Button>
				</div>
			</div>

			{visibleActions.length === 0 ? (
				<Card>
					<CardContent className="flex flex-col items-center gap-2 py-12 text-center">
						<CheckCircle2 className="h-10 w-10 text-emerald-600" />
						<p className="font-medium text-slate-900">
							{filter === "open" ? "No open tasks" : "No admin tasks found"}
						</p>
						<p className="text-sm text-slate-500">
							{filter === "open"
								? "All administrator tasks are currently resolved."
								: "Tasks created by operational workflows will appear here."}
						</p>
					</CardContent>
				</Card>
			) : (
				<div className="space-y-3">
					{visibleActions.map((action) => {
						const company = companies.find(
							(candidate) => candidate.id === action.companyId,
						);
						const isOpen = action.status === "open";
						const failure =
							action.type === "recording_processing_failed"
								? action.processingFailure
								: null;
						return (
							<Card
								key={action.id}
								className={isOpen ? "border-amber-200" : "border-slate-200"}
							>
								<CardHeader className="pb-3">
									<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
										<div className="flex min-w-0 gap-3">
											{isOpen ? (
												<AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
											) : (
												<CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
											)}
											<div className="min-w-0">
												<CardTitle className="text-base">
													{action.title}
												</CardTitle>
												<p className="mt-1 text-sm text-slate-600">
													{company?.name ||
														action.name ||
														action.companyId ||
														(failure
															? "Call processing"
															: "Registration Review")}
												</p>
											</div>
										</div>
										<Badge variant={isOpen ? "secondary" : "outline"}>
											{isOpen ? "Open" : "Resolved"}
										</Badge>
									</div>
								</CardHeader>
								<CardContent>
									<div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
										<dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
											<div>
												<dt className="text-slate-500">Type</dt>
												<dd className="font-medium text-slate-900">
													{failure
														? "Recording processing failure"
														: action.type.replaceAll("_", " ")}
												</dd>
											</div>
											<div>
												<dt className="text-slate-500">Created</dt>
												<dd className="text-slate-900">
													{displayDate(action.createdAt)}
												</dd>
											</div>
											{action.email ? (
												<div>
													<dt className="text-slate-500">Applicant Email</dt>
													<dd className="text-slate-900 font-mono text-xs">
														{action.email}
													</dd>
												</div>
											) : (
												<div>
													<dt className="text-slate-500">Due</dt>
													<dd className="text-slate-900">
														{displayDate(action.dueDate)}
													</dd>
												</div>
											)}
											{action.companyWebsite ? (
												<div>
													<dt className="text-slate-500">Website</dt>
													<dd className="text-slate-900 font-mono text-xs">
														{action.companyWebsite}
													</dd>
												</div>
											) : action.callId ? (
												<div>
													<dt className="text-slate-500">Call</dt>
													<dd className="font-mono text-xs text-slate-900">
														{action.caseId ? `Case ${action.caseId} · ` : ""}
														{action.callId}
													</dd>
												</div>
											) : (
												<div>
													<dt className="text-slate-500">Subscription</dt>
													<dd className="font-mono text-xs text-slate-900">
														{action.subscriptionId || "—"}
													</dd>
												</div>
											)}
											{failure && (
												<>
													<div className="min-w-0 sm:col-span-2 lg:col-span-4">
														<dt className="text-slate-500">Failure</dt>
														<dd className="mt-1 whitespace-pre-wrap break-words text-slate-900">
															{failure.errorMessage}
														</dd>
													</div>
													<div>
														<dt className="text-slate-500">Last failed</dt>
														<dd className="text-slate-900">
															{displayDate(failure.failedAt)}
														</dd>
													</div>
												</>
											)}
											{action.feedbackComment && (
												<div className="sm:col-span-2 lg:col-span-4">
													<dt className="text-slate-500">
														{action.type === "external_api_feedback"
															? "API feedback"
															: "Customer feedback"}
													</dt>
													<dd className="mt-1 whitespace-pre-wrap text-slate-900">
														{action.feedbackComment}
													</dd>
												</div>
											)}
											{action.apiFeedback && (
												<div className="sm:col-span-2 lg:col-span-4">
													<dt className="text-slate-500">Report context</dt>
													<dd className="mt-1 text-slate-900">
														{action.apiFeedback.category.replaceAll("_", " ")}
														{action.apiFeedback.relatedTool &&
															` · ${action.apiFeedback.relatedTool}`}
														{action.apiFeedback.relatedRequestId &&
															` · Request ${action.apiFeedback.relatedRequestId}`}
														{action.apiFeedback.clientId &&
															` · Client ${action.apiFeedback.clientId}`}
													</dd>
												</div>
											)}
										</dl>
										<div className="flex shrink-0 flex-wrap gap-2">
											{action.callId && (
												<Button variant="outline" asChild>
													<a
														href={
															failure
																? `/t-crds/${encodeURIComponent(action.callId)}?phoneNumber=${encodeURIComponent(failure.phoneNumber)}`
																: `/calls/${encodeURIComponent(action.callId)}`
														}
													>
														<ExternalLink className="h-4 w-4" />
														{failure
															? "Review and rerun processing"
															: "Review Call"}
													</a>
												</Button>
											)}
											<Button
												variant={isOpen ? "default" : "outline"}
												disabled={updatingId === action.id}
												onClick={() => toggleTask(action)}
											>
												{isOpen ? (
													<CheckCircle2 className="h-4 w-4" />
												) : (
													<CircleDot className="h-4 w-4" />
												)}
												{updatingId === action.id
													? "Updating…"
													: isOpen
														? "Mark Resolved"
														: "Mark Unresolved"}
											</Button>
										</div>
									</div>
								</CardContent>
							</Card>
						);
					})}
				</div>
			)}
		</div>
	);
}
